import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  ShieldAlert,
  ShieldOff,
  ExternalLink,
  AlertTriangle,
  Ban,
  CheckCircle2,
  Loader2,
  Info,
  Gavel,
  MapPin,
  Calendar,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import SEO from "../components/SEO";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC",
];

function BanBadge({ ban }) {
  if (!ban.has_ban) {
    return (
      <span
        className="inline-flex items-center gap-1.5 border border-zinc-400 bg-zinc-50 text-zinc-700 px-3 py-1 text-[11px] font-black uppercase tracking-widest"
        data-testid="ban-badge-unverified"
      >
        <Info size={12} /> Ban information not verified
      </span>
    );
  }
  if (ban.duration_type === "lifetime") {
    return (
      <span
        className="inline-flex items-center gap-1.5 border border-red-700 bg-red-700 text-white px-3 py-1 text-[11px] font-black uppercase tracking-widest"
        data-testid="ban-badge-lifetime"
      >
        <Ban size={12} /> Active — Lifetime
      </span>
    );
  }
  if (ban.duration_type === "time-limited") {
    return (
      <span
        className="inline-flex items-center gap-1.5 border border-orange-600 bg-orange-600 text-white px-3 py-1 text-[11px] font-black uppercase tracking-widest"
        data-testid="ban-badge-time-limited"
      >
        <ShieldAlert size={12} /> Active — Time-Limited
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 border border-zinc-900 bg-zinc-900 text-white px-3 py-1 text-[11px] font-black uppercase tracking-widest"
      data-testid="ban-badge-verified"
    >
      <ShieldAlert size={12} /> Ban Documented
    </span>
  );
}

function ResultCard({ item }) {
  const b = item.ban_info;
  const hasBan = b.has_ban;
  return (
    <div
      className={`border ${hasBan ? "border-zinc-900" : "border-zinc-300"} bg-white p-6 hover-shadow-harsh`}
      data-testid={`ban-result-${item.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow">
            {item.state_code} · {item.city || item.state}
          </div>
          <h3 className="font-display font-black text-2xl tracking-tight mt-1 leading-tight" data-testid={`ban-result-name-${item.id}`}>
            {item.name}
          </h3>
          {item.aliases && item.aliases.length > 0 && (
            <div className="text-[11px] font-mono text-zinc-500 mt-1">
              aka {item.aliases.join(" · ")}
            </div>
          )}
        </div>
        <BanBadge ban={b} />
      </div>

      {/* BAN DETAIL BLOCK */}
      <div className={`mt-5 border-l-4 pl-4 py-2 ${hasBan ? "border-red-700 bg-red-50" : "border-zinc-300 bg-zinc-50"}`}>
        <div className="label-eyebrow">Animal Ban Status</div>
        {hasBan ? (
          <>
            <div className="font-display font-black text-lg tracking-tight mt-1" data-testid={`ban-scope-${item.id}`}>
              {b.scope}
            </div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <div>
                <span className="text-zinc-500">Duration:</span>{" "}
                <span className="font-bold capitalize">{b.duration_type.replace("-", " ")}</span>
              </div>
              {b.effective_date && (
                <div data-testid={`ban-effective-${item.id}`}>
                  <span className="text-zinc-500">Effective:</span>{" "}
                  <span className="font-bold font-mono">{b.effective_date}</span>
                </div>
              )}
            </div>
            <div className="mt-3 text-[11px] text-zinc-600 leading-relaxed border-t border-red-200 pt-2">
              <span className="uppercase tracking-widest font-bold text-zinc-500">Source text:</span>{" "}
              <span className="italic">"{b.source_snippet}"</span>
            </div>
          </>
        ) : (
          <div className="mt-1 text-sm text-zinc-700">
            <span className="font-bold">Ban information not available / not verified.</span> The
            underlying case record does not document a court-ordered animal
            possession restriction. This is <b>not</b> a statement that no ban
            exists — only that PAWS on Record has not verified one.
          </div>
        )}
      </div>

      {/* CASE META */}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <div>
          <div className="flex items-center gap-2 text-zinc-500 text-[11px] uppercase tracking-widest font-bold">
            <Gavel size={12} /> Conviction
          </div>
          <div className="mt-1 leading-snug">
            <span className="font-bold">{item.status || "—"}</span>
            {item.conviction_year && <span className="text-zinc-500"> · {item.conviction_year}</span>}
          </div>
          {item.sentence && (
            <div className="mt-1 text-xs text-zinc-700 leading-relaxed">{item.sentence}</div>
          )}
        </div>
        <div>
          <div className="flex items-center gap-2 text-zinc-500 text-[11px] uppercase tracking-widest font-bold">
            <FileText size={12} /> Case Number
          </div>
          <div className="mt-1 font-mono text-sm font-bold" data-testid={`ban-case-${item.id}`}>
            {item.case_id || "—"}
          </div>
          <div className="flex items-center gap-2 text-zinc-500 text-[11px] uppercase tracking-widest font-bold mt-3">
            <MapPin size={12} /> Court / Jurisdiction
          </div>
          <div className="mt-1 text-sm">
            {item.city ? `${item.city}, ` : ""}
            {item.state}
          </div>
        </div>
      </div>

      {/* SOURCE + VIEW */}
      <div className="mt-5 flex flex-wrap items-center gap-3 pt-4 border-t border-zinc-200">
        {item.source_url ? (
          <a
            href={item.source_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-zinc-900 hover:text-red-600"
            data-testid={`ban-source-${item.id}`}
          >
            <ExternalLink size={12} /> Verified source
          </a>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500 font-bold">
            <Info size={12} /> No public URL provided
          </span>
        )}
        <Link
          to={`/abuser/${item.id}`}
          className="ml-auto btn-outline text-xs"
          data-testid={`ban-view-case-${item.id}`}
        >
          View Full Case →
        </Link>
      </div>
    </div>
  );
}

export default function AnimalBanLookup() {
  const [name, setName] = useState("");
  const [caseNum, setCaseNum] = useState("");
  const [state, setState] = useState("");
  const [county, setCounty] = useState("");
  const [onlyWithBan, setOnlyWithBan] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [withBan, setWithBan] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  const runSearch = async (e) => {
    if (e) e.preventDefault();
    // At least one filter required
    if (!name && !caseNum && !state && !county) {
      setErrorMsg("Enter at least one search term: name, case number, state, or county.");
      toast.error("Add a search term");
      return;
    }
    setErrorMsg("");
    setLoading(true);
    try {
      const { data } = await api.get("/ban-lookup", {
        params: {
          q: name || undefined,
          case_id: caseNum || undefined,
          state: state || undefined,
          county: county || undefined,
          only_with_ban: onlyWithBan,
          limit: 50,
        },
      });
      setResults(data.items || []);
      setTotal(data.total || 0);
      setWithBan(data.with_ban || 0);
      setSearched(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Search failed. Please try again in a moment.";
      setErrorMsg(detail);
      toast.error(detail);
      setSearched(true);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const clearAll = () => {
    setName("");
    setCaseNum("");
    setState("");
    setCounty("");
    setResults([]);
    setSearched(false);
    setErrorMsg("");
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10" data-testid="ban-lookup-page">
      <SEO
        title="Animal Ban Lookup · Check if someone has a court-ordered animal ban"
        description="Search Paws on Record for court-ordered animal-possession bans. Verified from convicted-case records — never inferred from the offense alone. Use to screen adopters, fosters and rescues."
        path="/animal-ban-lookup"
      />

      <div className="label-eyebrow">Section · Prevention Tool</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
        Animal Ban Lookup.
      </h1>
      <p className="text-lg text-zinc-700 mt-4 max-w-3xl leading-relaxed">
        Check whether a person has a{" "}
        <span className="font-bold underline decoration-red-600 decoration-2 underline-offset-2">
          verified court-ordered restriction on possessing animals
        </span>
        . Every ban shown is extracted verbatim from a convicted PAWS on
        Record case — nothing is inferred from the offense alone.
      </p>

      <form
        onSubmit={runSearch}
        className="mt-8 border border-zinc-900 bg-white p-6"
        data-testid="ban-lookup-form"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label-eyebrow block mb-2">Offender name</label>
            <div className="flex items-center border border-zinc-900">
              <Search size={16} className="ml-3 text-zinc-400" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full or partial name (e.g. Kaitlynn Allen)"
                className="w-full px-3 py-3 outline-none text-base"
                data-testid="ban-lookup-name"
              />
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Case number</label>
            <div className="flex items-center border border-zinc-900">
              <FileText size={16} className="ml-3 text-zinc-400" />
              <input
                type="text"
                value={caseNum}
                onChange={(e) => setCaseNum(e.target.value)}
                placeholder="e.g. 24-1-00979-31"
                className="w-full px-3 py-3 outline-none text-base font-mono"
                data-testid="ban-lookup-case"
              />
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">State</label>
            <select
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="w-full border border-zinc-900 px-3 py-3 outline-none bg-white text-base"
              data-testid="ban-lookup-state"
            >
              <option value="">All states</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">County / Jurisdiction</label>
            <div className="flex items-center border border-zinc-900">
              <MapPin size={16} className="ml-3 text-zinc-400" />
              <input
                type="text"
                value={county}
                onChange={(e) => setCounty(e.target.value)}
                placeholder="e.g. Snohomish, Pierce, King"
                className="w-full px-3 py-3 outline-none text-base"
                data-testid="ban-lookup-county"
              />
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-4">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="ban-lookup-only-with-ban-label">
            <input
              type="checkbox"
              checked={onlyWithBan}
              onChange={(e) => setOnlyWithBan(e.target.checked)}
              className="w-4 h-4 accent-red-600"
              data-testid="ban-lookup-only-with-ban"
            />
            Only show records with a verified ban
          </label>

          <div className="ml-auto flex items-center gap-2">
            {(name || caseNum || state || county) && (
              <button
                type="button"
                onClick={clearAll}
                className="btn-outline text-xs"
                data-testid="ban-lookup-clear"
              >
                Clear
              </button>
            )}
            <button
              type="submit"
              disabled={loading}
              className="btn-danger disabled:opacity-60"
              data-testid="ban-lookup-submit"
            >
              {loading ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Searching
                </>
              ) : (
                <>
                  <Search size={14} /> Search Bans
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {errorMsg && (
        <div
          className="mt-8 border border-red-600 bg-red-50 text-red-900 p-4 flex items-start gap-3"
          role="alert"
          data-testid="ban-lookup-error"
        >
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div className="text-sm leading-relaxed">{errorMsg}</div>
        </div>
      )}

      {searched && !errorMsg && (
        <div className="mt-8">
          {results.length > 0 ? (
            <>
              <div
                className="flex flex-wrap items-center gap-3 justify-between border-b border-zinc-900 pb-3 mb-6"
                data-testid="ban-lookup-summary"
              >
                <div>
                  <span className="font-display font-black text-2xl">{total}</span>{" "}
                  <span className="label-eyebrow">
                    match{total === 1 ? "" : "es"}
                  </span>
                  {withBan > 0 && (
                    <span className="ml-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-red-700">
                      <Ban size={12} /> {withBan} with verified ban
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-zinc-500 uppercase tracking-widest">
                  Sorted: verified bans first
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="ban-lookup-results">
                {results.map((it) => (
                  <ResultCard key={it.id} item={it} />
                ))}
              </div>
            </>
          ) : (
            <div
              className="border border-zinc-900 bg-zinc-50 p-10 text-center"
              data-testid="ban-lookup-no-results"
            >
              <ShieldOff size={40} className="mx-auto text-zinc-400" />
              <div className="font-display font-black text-2xl mt-4">
                No matching verified record was found in the PAWS on Record database.
              </div>
              <p className="text-sm text-zinc-600 mt-3 max-w-xl mx-auto leading-relaxed">
                This is <b>not</b> a statement that the person has no animal ban.
                It only means PAWS on Record has not verified one from a court
                record. Try broadening the search (fewer filters or partial
                name), or report a known conviction so we can add it.
              </p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                <Link to="/report" className="btn-danger text-xs" data-testid="ban-lookup-report-cta">
                  Report a Case
                </Link>
                <Link to="/" className="btn-outline text-xs">
                  Browse Full Registry
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      {!searched && (
        <div
          className="mt-10 border border-zinc-300 bg-zinc-50 p-8"
          data-testid="ban-lookup-hint"
        >
          <div className="flex items-start gap-3">
            <CheckCircle2 size={22} className="text-red-600 mt-1 shrink-0" />
            <div>
              <div className="font-display font-black text-xl tracking-tight">
                How this protects animals.
              </div>
              <p className="text-sm text-zinc-700 mt-3 max-w-2xl leading-relaxed">
                Shelters, rescues and foster networks can use this tool to
                screen potential adopters. Every result is drawn from a
                verified court case. The <b>scope</b> of the ban is preserved
                exactly as documented — a "Lifetime DOG Ban" is never displayed
                as a "Lifetime Animal Ban."
              </p>
              <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-bold uppercase tracking-widest">
                <span className="inline-flex items-center gap-1.5 border border-red-700 bg-red-700 text-white px-2.5 py-1">
                  <Ban size={11} /> Lifetime
                </span>
                <span className="inline-flex items-center gap-1.5 border border-orange-600 bg-orange-600 text-white px-2.5 py-1">
                  <ShieldAlert size={11} /> Time-limited
                </span>
                <span className="inline-flex items-center gap-1.5 border border-zinc-400 bg-zinc-50 text-zinc-700 px-2.5 py-1">
                  <Info size={11} /> Not verified
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
